import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import TouchButton from '../ui/TouchButton';
import ReviewWizard from '../ReviewWizard';
import MapSearchOverlay from '../search/MapSearchOverlay';

// Fix for default markers in React Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Touch-optimized map controls
const TouchMapControls = ({ onZoomIn, onZoomOut, onLocate, onFullscreen, isFullscreen }) => {
  const controlsStyles = {
    position: 'absolute',
    top: '16px',
    right: '16px',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  };

  const controlButtonStyles = {
    width: '48px',
    height: '48px',
    backgroundColor: '#ffffff',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    transition: 'all 150ms ease-in-out',
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent'
  };

  const ControlButton = ({ onClick, children, active = false }) => {
    const [isPressed, setIsPressed] = useState(false);
    
    return (
      <button
        style={{
          ...controlButtonStyles,
          backgroundColor: active ? '#2196F3' : isPressed ? '#f5f5f5' : '#ffffff',
          color: active ? '#ffffff' : '#333333',
          transform: isPressed ? 'scale(0.95)' : 'scale(1)'
        }}
        onClick={onClick}
        onTouchStart={() => setIsPressed(true)}
        onTouchEnd={() => setIsPressed(false)}
        onMouseDown={() => setIsPressed(true)}
        onMouseUp={() => setIsPressed(false)}
        onMouseLeave={() => setIsPressed(false)}
      >
        {children}
      </button>
    );
  };

  return (
    <div style={controlsStyles}>
      <ControlButton onClick={onZoomIn}>+</ControlButton>
      <ControlButton onClick={onZoomOut}>−</ControlButton>
      <ControlButton onClick={onLocate}>📍</ControlButton>
      <ControlButton onClick={onFullscreen} active={isFullscreen}>
        {isFullscreen ? '⤓' : '⤢'}
      </ControlButton>
    </div>
  );
};

// Search overlay for mobile
const TouchSearchOverlay = ({ onSearch, isVisible, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [recentSearches, setRecentSearches] = useState([]);

  useEffect(() => {
    // Load recent searches from localStorage
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  const handleSearch = (term) => {
    if (term.trim()) {
      const newSearches = [term, ...recentSearches.filter(s => s !== term)].slice(0, 5);
      setRecentSearches(newSearches);
      localStorage.setItem('recentSearches', JSON.stringify(newSearches));
      onSearch(term);
      onClose();
    }
  };

  if (!isVisible) return null;

  const overlayStyles = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '16px'
  };

  const searchContainerStyles = {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '20px',
    width: '100%',
    maxWidth: '400px',
    marginTop: '20px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
  };

  const searchInputStyles = {
    width: '100%',
    padding: '12px 16px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '16px',
    outline: 'none',
    marginBottom: '16px'
  };

  const recentItemStyles = {
    padding: '12px 16px',
    backgroundColor: '#f5f5f5',
    border: 'none',
    borderRadius: '8px',
    width: '100%',
    textAlign: 'left',
    fontSize: '14px',
    cursor: 'pointer',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  return (
    <div style={overlayStyles} onClick={onClose}>
      <div style={searchContainerStyles} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', flex: 1 }}>Search Location</h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666666'
            }}
          >
            ×
          </button>
        </div>
        
        <input
          type="text"
          style={searchInputStyles}
          placeholder="Search for a location..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch(searchTerm)}
          autoFocus
        />
        
        <TouchButton
          onClick={() => handleSearch(searchTerm)}
          fullWidth
          disabled={!searchTerm.trim()}
        >
          Search
        </TouchButton>
        
        {recentSearches.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666666' }}>
              Recent Searches
            </h4>
            {recentSearches.map((search, index) => (
              <button
                key={index}
                style={recentItemStyles}
                onClick={() => handleSearch(search)}
              >
                <span>🕒</span>
                <span>{search}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Location info popup
const TouchLocationPopup = ({ location, onClose, onAddReview }) => {
  const popupStyles = {
    position: 'absolute',
    bottom: '20px',
    left: '16px',
    right: '16px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    zIndex: 1500,
    maxHeight: '50vh',
    overflowY: 'auto'
  };

  const headerStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px'
  };

  const titleStyles = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#212121',
    margin: 0,
    flex: 1
  };

  const closeButtonStyles = {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#666666',
    padding: '4px'
  };

  const safetyScoreStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px'
  };

  const ScoreIndicator = ({ score }) => {
    const getScoreColor = (score) => {
      if (score >= 8) return '#4CAF50';
      if (score >= 6) return '#FF9800';
      if (score >= 4) return '#FF5722';
      return '#F44336';
    };

    const getScoreLabel = (score) => {
      if (score >= 8) return 'Very Safe';
      if (score >= 6) return 'Generally Safe';
      if (score >= 4) return 'Use Caution';
      return 'High Risk';
    };

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        backgroundColor: getScoreColor(score),
        color: '#ffffff',
        borderRadius: '20px',
        fontSize: '14px',
        fontWeight: '600'
      }}>
        <span>{score.toFixed(1)}/10</span>
        <span>•</span>
        <span>{getScoreLabel(score)}</span>
      </div>
    );
  };

  return (
    <div style={popupStyles}>
      <div style={headerStyles}>
        <h3 style={titleStyles}>{location.name}</h3>
        <button style={closeButtonStyles} onClick={onClose}>×</button>
      </div>
      
      <div style={safetyScoreStyles}>
        <ScoreIndicator score={location.safetyScore || 7.5} />
      </div>
      
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '4px' }}>🔦</div>
          <div style={{ fontSize: '12px', color: '#666666' }}>Lighting</div>
          <div style={{ fontSize: '14px', fontWeight: '600' }}>Good</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '4px' }}>👥</div>
          <div style={{ fontSize: '12px', color: '#666666' }}>Crowd</div>
          <div style={{ fontSize: '14px', fontWeight: '600' }}>Moderate</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '4px' }}>🚔</div>
          <div style={{ fontSize: '12px', color: '#666666' }}>Police</div>
          <div style={{ fontSize: '14px', fontWeight: '600' }}>Present</div>
        </div>
      </div>
      
      <div style={{
        display: 'flex',
        gap: '12px',
        marginTop: '16px'
      }}>
        <TouchButton
          variant="secondary"
          onClick={() => {/* Navigate to location */}}
          fullWidth
        >
          Get Directions
        </TouchButton>
        <TouchButton
          variant="primary"
          onClick={onAddReview}
          fullWidth
        >
          📝 Add Review
        </TouchButton>
      </div>
    </div>
  );
};

// Map event handlers
const MapEventHandler = ({ onMapClick, onMapMove, onZoomChange }) => {
  const map = useMap();
  
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng);
    },
    moveend: () => {
      onMapMove(map.getCenter(), map.getZoom());
    },
    zoomend: () => {
      onZoomChange(map.getZoom());
    }
  });

  return null;
};

// Main touch-optimized map component
const TouchOptimizedMap = ({ 
  center = [40.7128, -74.0060], 
  zoom = 10, 
  safetyZones = [],
  onLocationSelect,
  onAddReview,
  className = '' 
}) => {
  const [mapCenter, setMapCenter] = useState(center);
  const [mapZoom, setMapZoom] = useState(zoom);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [isLocating, setIsLocating] = useState(false);
  const [showReviewWizard, setShowReviewWizard] = useState(false);
  const [reviewWizardData, setReviewWizardData] = useState({});
  const [showMapSearch, setShowMapSearch] = useState(false);
  const [mapBounds, setMapBounds] = useState(null);
  const mapRef = useRef(null);

  // Get user location
  const getUserLocation = useCallback(() => {
    if (isLocating) return;
    
    setIsLocating(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const newCenter = [latitude, longitude];
          setUserLocation(newCenter);
          setMapCenter(newCenter);
          setMapZoom(15);
          setIsLocating(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          setIsLocating(false);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setIsLocating(false);
    }
  }, [isLocating]);

  // Map controls
  const handleZoomIn = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.setZoom(mapZoom + 1);
    }
  }, [mapZoom]);

  const handleZoomOut = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.setZoom(mapZoom - 1);
    }
  }, [mapZoom]);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);

  const handleMapClick = useCallback((latlng) => {
    // Mock location data - in real app, this would come from API
    const location = {
      name: 'Selected Location',
      coordinates: latlng,
      safetyScore: 7.5,
      lighting: 'Good',
      crowd: 'Moderate',
      police: 'Present'
    };
    
    setSelectedLocation(location);
    if (onLocationSelect) {
      onLocationSelect(location);
    }
  }, [onLocationSelect]);

  const handleMapMove = useCallback((center, zoom) => {
    setMapCenter(center);
    setMapZoom(zoom);
    
    // Update bounds for search
    if (mapRef.current) {
      const bounds = mapRef.current.getBounds();
      setMapBounds(bounds);
    }
  }, []);

  const handleSearch = useCallback((searchTerm) => {
    // Mock search - in real app, this would geocode the search term
    console.log('Searching for:', searchTerm);
    // For demo, just center on a random location
    const randomLat = 40.7128 + (Math.random() - 0.5) * 0.1;
    const randomLng = -74.0060 + (Math.random() - 0.5) * 0.1;
    setMapCenter([randomLat, randomLng]);
    setMapZoom(15);
  }, []);

  const handleLocationSelect = useCallback((location) => {
    // Center map on selected location
    if (location.coordinates) {
      const coords = Array.isArray(location.coordinates) 
        ? location.coordinates 
        : [location.coordinates.coordinates[1], location.coordinates.coordinates[0]]; // Convert from [lng, lat] to [lat, lng]
      
      setMapCenter(coords);
      setMapZoom(16);
      
      // Set as selected location for popup
      setSelectedLocation({
        ...location,
        coordinates: coords
      });
    }

    if (onLocationSelect) {
      onLocationSelect(location);
    }
  }, [onLocationSelect]);

  const mapContainerStyles = {
    position: 'relative',
    width: '100%',
    height: isFullscreen ? '100vh' : '100%',
    minHeight: '400px',
    backgroundColor: '#f5f5f5'
  };

  const searchButtonStyles = {
    position: 'absolute',
    top: '16px',
    left: '16px',
    zIndex: 1000,
    backgroundColor: '#ffffff',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    borderRadius: '8px',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    minWidth: '200px',
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent'
  };

  return (
    <div style={mapContainerStyles} className={className}>
      {/* Search Button */}
      <button
        style={searchButtonStyles}
        onClick={() => setShowMapSearch(true)}
      >
        <span>🔍</span>
        <span>Search locations...</span>
      </button>

      {/* Map */}
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        style={{ width: '100%', height: '100%' }}
        ref={mapRef}
        zoomControl={false}
        attributionControl={false}
        touchZoom={true}
        doubleClickZoom={true}
        scrollWheelZoom={true}
        boxZoom={true}
        keyboard={true}
        dragging={true}
        tap={true}
        tapTolerance={15}
        worldCopyJump={true}
        maxBoundsViscosity={1.0}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        
        {/* User location marker */}
        {userLocation && (
          <Marker position={userLocation}>
            <Popup>
              Your current location
            </Popup>
          </Marker>
        )}
        
        {/* Safety zone markers */}
        {safetyZones.map((zone, index) => (
          <Marker key={index} position={zone.center}>
            <Popup>
              <div>
                <h4>{zone.name}</h4>
                <p>Safety Score: {zone.safetyScore}/10</p>
              </div>
            </Popup>
          </Marker>
        ))}
        
        <MapEventHandler
          onMapClick={handleMapClick}
          onMapMove={handleMapMove}
          onZoomChange={setMapZoom}
        />
      </MapContainer>

      {/* Touch Controls */}
      <TouchMapControls
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onLocate={getUserLocation}
        onFullscreen={toggleFullscreen}
        isFullscreen={isFullscreen}
      />

      {/* Floating Action Button for Quick Review */}
      <button
        style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          width: '64px',
          height: '64px',
          backgroundColor: '#2196F3',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          fontSize: '24px',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(33, 150, 243, 0.4)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 200ms ease',
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent'
        }}
        onClick={() => {
          setReviewWizardData({
            locationName: userLocation ? 'Current Location' : '',
            coordinates: userLocation ? {
              lat: userLocation[0],
              lng: userLocation[1]
            } : null
          });
          setShowReviewWizard(true);
        }}
        onTouchStart={(e) => {
          e.target.style.transform = 'scale(0.95)';
          e.target.style.boxShadow = '0 2px 8px rgba(33, 150, 243, 0.6)';
        }}
        onTouchEnd={(e) => {
          e.target.style.transform = 'scale(1)';
          e.target.style.boxShadow = '0 4px 12px rgba(33, 150, 243, 0.4)';
        }}
      >
        📝
      </button>

      {/* Map Search Overlay */}
      <MapSearchOverlay
        isVisible={showMapSearch}
        onClose={() => setShowMapSearch(false)}
        onLocationSelect={handleLocationSelect}
        mapCenter={mapCenter}
        mapBounds={mapBounds}
        userLocation={userLocation}
      />

      {/* Legacy Search Overlay */}
      <TouchSearchOverlay
        isVisible={showSearch}
        onClose={() => setShowSearch(false)}
        onSearch={handleSearch}
      />

      {/* Location Popup */}
      {selectedLocation && (
        <TouchLocationPopup
          location={selectedLocation}
          onClose={() => setSelectedLocation(null)}
          onAddReview={() => {
            setReviewWizardData({
              locationName: selectedLocation.name,
              coordinates: {
                lat: selectedLocation.coordinates[0],
                lng: selectedLocation.coordinates[1]
              }
            });
            setShowReviewWizard(true);
            setSelectedLocation(null);
          }}
        />
      )}

      {/* Review Wizard Modal */}
      {showReviewWizard && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#f8f9fa',
          zIndex: 3000,
          overflow: 'auto'
        }}>
          <ReviewWizard
            initialData={reviewWizardData}
            onSubmit={(reviewData) => {
              console.log('Review submitted from map:', reviewData);
              setShowReviewWizard(false);
              setReviewWizardData({});
              if (onAddReview) {
                onAddReview(reviewData);
              }
            }}
            onCancel={() => {
              setShowReviewWizard(false);
              setReviewWizardData({});
            }}
          />
        </div>
      )}

      {/* Loading overlay for location */}
      {isLocating && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #e0e0e0',
              borderTop: '4px solid #2196F3',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 12px'
            }}></div>
            <div>Getting your location...</div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default TouchOptimizedMap;